import { Link } from "react-router-dom";
import { ArrowUp } from "lucide-react";

const LAST_UPDATED = "27 August 2026";

const A = ({ href, external, children }) => (
  <a
    href={href}
    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    className="font-medium text-melt-teal underline underline-offset-2 hover:text-melt-teal-600"
  >
    {children}
    {external && <span className="sr-only"> (opens in a new tab)</span>}
  </a>
);

const SECTIONS = [
  {
    id: "who-we-are",
    title: "Who we are",
    content: (
      <>
        <p>
          This website hosts the MultiEthno-Linguistic Toolkit (MELT), a freely available research
          toolkit developed by researchers at the University of Plymouth, the University of Southampton
          and Manipal University College Malaysia.
        </p>
        <p>
          The data controller for personal information collected through this website is the University
          of Plymouth. Enquiries about this policy may be addressed to Dr. Sumanth Kumbargere Nagraj,
          Peninsula Dental School, University of Plymouth, Drake Circus, PL4 8AA, Plymouth, United
          Kingdom, or by email to{" "}
          <A href="mailto:sumanth.kumbargere@plymouth.ac.uk">sumanth.kumbargere@plymouth.ac.uk</A>. Formal
          data protection enquiries may also be directed to the Data Protection Officer of the University
          of Plymouth.
        </p>
      </>
    ),
  },
  {
    id: "our-approach",
    title: "Our approach to personal information",
    content: (
      <>
        <p>
          We handle personal information, research data and unpublished materials with strict
          confidentiality and in full alignment with the UK General Data Protection Regulation, the Data
          Protection Act 2018 and the EU General Data Protection Regulation. We collect only what is
          necessary for the purpose stated at the point of collection, apply pseudonymisation wherever it
          is possible to do so, protect information through appropriate technical and organisational
          measures, and retain it only for as long as it is required.
        </p>
        <p>
          Where we process personal data in the course of collaborative research, we do so in accordance
          with the ethical approvals governing that research and under written agreements with our partner
          institutions.
        </p>
      </>
    ),
  },
  {
    id: "what-we-collect",
    title: "What we collect and why",
    content: (
      <>
        <p>
          <strong className="text-melt-indigo">Enquiries submitted through the contact form.</strong> If
          you contact us, we collect the name and email address you provide, and any further details you
          choose to give, such as your institution, your country, and the content of your message. We use
          this information solely to respond to your enquiry and, where you have offered feedback, to
          inform the continued development of the toolkit. Our lawful basis for this processing is
          legitimate interests, namely responding to correspondence and improving a publicly available
          research resource.
        </p>
        <p>
          <strong className="text-melt-indigo">Downloads of toolkit materials.</strong> We record the
          total number of times each document is downloaded. This is a simple count and is not linked to
          any individual. We do not record your name, your email address or your internet protocol address
          in connection with a download.
        </p>
        <p>
          <strong className="text-melt-indigo">Website usage.</strong> We use analytics that do not set
          cookies and do not track individual visitors. The information gathered is aggregated and tells us
          only which pages are being read and which materials are being used.
        </p>
        <p>
          We do not sell personal information, we do not use it for marketing, and we do not share it with
          third parties for their own purposes.
        </p>
      </>
    ),
  },
  {
    id: "how-long-we-keep",
    title: "How long we keep information",
    content: (
      <>
        <p>
          Enquiries received through the contact form are retained for up to twenty four months from the
          date of the last correspondence, after which they are deleted. Where feedback has informed a
          revision of the toolkit, we retain only the anonymised substance of that feedback and not the
          personal details of the person who provided it.
        </p>
      </>
    ),
  },
  {
    id: "international-transfers",
    title: "International transfers",
    content: (
      <>
        <p>
          If personal data ever needs to be transferred outside the United Kingdom or the European
          Economic Area, we will do so only where a legally approved safeguard is in place. These may
          include an adequacy decision, or the required contractual protections such as the EU Standard
          Contractual Clauses or the UK International Data Transfer Agreement. Our website hosting provider
          may store data outside the United Kingdom, and appropriate safeguards are in place with that
          provider.
        </p>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "Your rights",
    content: (
      <>
        <p>
          You have the right to request access to the personal information we hold about you, and to
          request its rectification, erasure, restriction or portability. You may also object to our
          processing of your information. To exercise any of these rights, please write to the
          correspondence address given above and we will respond within one month.
        </p>
        <p>
          If you are not satisfied with our response, you have the right to complain to the Information
          Commissioner’s Office, the supervisory authority for data protection in the United Kingdom, at{" "}
          <A href="https://ico.org.uk" external>ico.org.uk</A>.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies and similar technologies",
    content: (
      <>
        <p>
          This website uses cookies only where they are strictly necessary for the site to function. We do
          not use advertising cookies, tracking cookies or third party marketing cookies, and we do not
          build profiles of our visitors.
        </p>
        <h3 className="mt-6 font-serif text-xl font-semibold text-melt-indigo">Strictly necessary cookies</h3>
        <p>
          <strong className="text-melt-indigo">Security.</strong> A cookie may be set to protect the
          contact form against automated submissions and to maintain the security of your connection while
          you are using the site.
        </p>
        <p>
          <strong className="text-melt-indigo">Administrative access.</strong> Where a member of the
          toolkit team signs in to the administrative area of this website in order to read enquiries or
          update the toolkit materials, a secure session cookie named session_token is set to maintain that
          session. This cookie expires after seven days of inactivity and is never set for ordinary
          visitors to the site.
        </p>
        <p>
          Because these cookies are strictly necessary for the operation of the website, they do not
          require your consent and cannot be disabled from within the site. You may manage or block cookies
          through your browser settings, although doing so may prevent parts of this website from working
          correctly.
        </p>
      </>
    ),
  },
  {
    id: "changes-to-this-policy",
    title: "Changes to this policy",
    content: (
      <>
        <p>
          We may update this policy from time to time. The date at the top of this page shows when it was
          last revised.
        </p>
      </>
    ),
  },
];

export default function DataProtection() {
  return (
    <>
      {/* Slim document header */}
      <section id="policy-top" className="woven-texture" data-testid="policy-header">
        <div className="mx-auto max-w-3xl px-5 py-14 md:px-8 md:py-16">
          <h1 className="font-serif text-4xl font-semibold text-[#FAF8F5] sm:text-5xl">
            Data Protection and Cookie Policy
          </h1>
          <p className="mt-4 text-[15px] text-[#FAF8F5]/70" data-testid="policy-last-updated">
            Last updated: {LAST_UPDATED}
          </p>
        </div>
      </section>

      {/* Single readable column, ~70 characters wide */}
      <div className="mx-auto w-full max-w-[70ch] px-5 py-14 md:px-8 md:py-16">
        {/* Table of contents */}
        <nav aria-label="On this page" className="rounded-lg border border-melt-grey bg-white p-6" data-testid="policy-toc">
          <h2 className="font-serif text-lg font-semibold">On this page</h2>
          <ol className="mt-4 space-y-2">
            {SECTIONS.map((s, i) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="text-melt-teal underline underline-offset-2 hover:text-melt-teal-600"
                  data-testid={`toc-link-${s.id}`}
                >
                  {i + 1}. {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Sections */}
        <div className="mt-12 space-y-12">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24" data-testid={`policy-section-${s.id}`}>
              <h2 className="font-serif text-2xl font-semibold text-melt-indigo">{s.title}</h2>
              <div className="mt-4 space-y-4 text-[17px] leading-relaxed text-melt-indigo/85 [&_p]:text-[17px]">
                {s.content}
              </div>
              {/* Back to top — shown on screens wider than 768px */}
              <a
                href="#policy-top"
                className="no-print mt-5 hidden items-center gap-1.5 text-sm font-medium text-melt-teal underline underline-offset-2 hover:text-melt-teal-600 md:inline-flex"
                data-testid={`back-to-top-${s.id}`}
              >
                <ArrowUp size={15} aria-hidden="true" />
                Back to top
              </a>
            </section>
          ))}
        </div>

        <p className="mt-14 border-t border-melt-grey pt-6 text-[15px] text-melt-indigo/70">
          Return to the{" "}
          <Link to="/" className="text-melt-teal underline underline-offset-2 hover:text-melt-teal-600">
            MELT home page
          </Link>
          .
        </p>
      </div>
    </>
  );
}
